import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { SagepayPiFormComponentDef } from '@df/module-sagepay';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import chunk from 'lodash-es/chunk';
import { CheckoutFormComponent } from '../../../checkout/checkout-form.component';
import { CheckoutStepComponent } from '../../../checkout/checkout-step.component';
import { CheckboxComponent } from '../../../ui/form/checkbox';
import { InputWrapBodyDirective } from '../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../ui/form/input-wrap.component';
import { MuiInputDirective } from '../../../ui/form/mui-input.directive';
import { SagepayPiServiceDecorator } from './sagepay.service.decorator';

@Component({
    selector: 'sagepay-pi-form',
    templateUrl: './sagepay-pi-form.html',
    standalone: true,
    imports: [CheckboxComponent, FormsModule, InputWrapBodyDirective, InputWrapComponent, MuiInputDirective, TranslatePipe],
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class SagepayPiFormComponent extends SagepayPiFormComponentDef {
    /**
     * Date regexp
     * @type {RegExp}
     */
    protected _datePattern?: RegExp;

    sagepayPiServiceDecorator = inject(SagepayPiServiceDecorator);

    constructor(
        protected override checkoutFormComponent: CheckoutFormComponent,
        protected override changeDetectorRef: ChangeDetectorRef,
        protected override scrollToService: ScrollToService,
        protected override checkoutStepComponent: CheckoutStepComponent
    ) {
        super(checkoutFormComponent, changeDetectorRef, scrollToService, checkoutStepComponent);
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.sagepayPiServiceDecorator.selectedCardType = undefined;
    }

    /**
     * Returns date pattern
     * @returns {RegExp}
     */
    get datePattern(): RegExp {
        if (!this._datePattern) {
            const now = new Date();
            const currentYear = now.getFullYear().toString().slice(-2);
            const first = parseInt(currentYear[0], 10);
            const second = parseInt(currentYear[1], 10);
            // we required date in format MM/YY
            return new RegExp(`^(1[0-2]|0[1-9])\/([${first}-9][${second}-9]|[${first + 1}-9][0-9])$`);
        }
        return this._datePattern;
    }

    /**
     * Date model getter
     * @returns {string}
     */
    get ccDate(): string {
        return this.data.date || '';
    }

    /**
     * Date model setter
     */
    set ccDate(value: string) {
        if (value.length === 2 && this.data.date?.length === 1) {
            value = value + '/';
        }
        this.data.date = value;
        if (typeof value === 'string') {
            // we remove non number characters get month and year from it
            const numbersOnly = value.replace(/\D/g, '');
            const month = numbersOnly.slice(0, 2);
            const year = numbersOnly.slice(2, 4);
            this.data.expiryMonth = month.length === 2 ? month : undefined;
            this.data.expiryYear = year.length === 2 ? year : undefined;
        } else {
            this.data.expiryMonth = undefined;
            this.data.expiryYear = undefined;
        }

        this.changeDetectorRef.detectChanges();
    }

    override get ccType(): string | undefined {
        return this.getCcType();
    }

    override set ccType(ccType: string | undefined) {
        this._ccType = ccType;
        this.updateSelectedCardType();
    }

    updateSelectedCardType(): void {
        this.sagepayPiServiceDecorator.selectedCardType = this.ccType;
    }

    /**
     * Card number getter
     * @returns {string}
     */
    override get ccNumber(): string {
        return this.data.number;
    }

    /**
     * Card number setter
     */
    override set ccNumber(value: string) {
        this.data.number = chunk((value || '').replace(/\s/g, ''), 4)
            .map(i => i.join(''))
            .join(' ');
        this.ccType = this.identifyCcType(this.getCcNumber());
    }
}
